"use client";

/**
 * The one option card for every intake selection step.
 *
 * Problems, workflows, metrics and products all render this, so an option's
 * description always sits in the same place — directly beneath its name. The
 * chrome is the compact card the problem step already used; the indicator
 * shape follows the step's cardinality, so a square means "pick several" and
 * a circle means "pick one".
 *
 * The visual frame is neutral. Its label contains a native checkbox/radio,
 * while the optional `meta` control is a sibling interaction surface.
 */

import {
  createContext,
  type KeyboardEvent,
  type ReactNode,
  useContext,
} from "react";
import { Check } from "../lib/icons";

export type IntakeOptionIndicator = "checkbox" | "radio";

const IntakeOptionInputNameContext = createContext<string | undefined>(
  undefined,
);

/** Internal provider used by radio-list owners and their trailing refine row. */
export function IntakeOptionInputNameProvider({
  name,
  children,
}: {
  name: string;
  children: ReactNode;
}) {
  return (
    <IntakeOptionInputNameContext.Provider value={name}>
      {children}
    </IntakeOptionInputNameContext.Provider>
  );
}

export interface IntakeOptionCardProps {
  name: string;
  description?: string;
  /** Cardinality of the step → indicator shape and ARIA role. */
  indicator: IntakeOptionIndicator;
  selected: boolean;
  onSelect: () => void;
  /** Muted text beside the name: a problem's category, a product's vendor. */
  secondaryLabel?: string;
  /** Leading visual before the text column, e.g. a product's avatar mark. */
  adornment?: ReactNode;
  /** Trailing controls, e.g. the metric `fx` popover. Clicks do not select. */
  meta?: ReactNode;
  /** Extra content below the description, e.g. "Addressed by: …". */
  footer?: ReactNode;
}

export function IntakeOptionCard({
  name,
  description,
  indicator,
  selected,
  onSelect,
  secondaryLabel,
  adornment,
  meta,
  footer,
}: IntakeOptionCardProps) {
  const inputName = useContext(IntakeOptionInputNameContext);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (indicator === "checkbox" || !selected) onSelect();
      return;
    }

    if (
      indicator !== "radio" ||
      !inputName ||
      !["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft"].includes(event.key)
    ) {
      return;
    }

    event.preventDefault();
    const radios = Array.from(
      event.currentTarget.ownerDocument.getElementsByName(inputName),
    ).filter(
      (element): element is HTMLInputElement =>
        element.tagName === "INPUT" &&
        (element as HTMLInputElement).type === "radio" &&
        !(element as HTMLInputElement).disabled,
    );
    const currentIndex = radios.indexOf(event.currentTarget);
    if (currentIndex === -1 || radios.length === 0) return;
    const direction =
      event.key === "ArrowDown" || event.key === "ArrowRight" ? 1 : -1;
    const next =
      radios[(currentIndex + direction + radios.length) % radios.length];
    if (!next) return;
    next.focus();
    next.click();
  }

  return (
    <div
      className={[
        "flex w-full items-start gap-[11px] text-left",
        "rounded-[12px] border bg-(--bg-white) px-[14px] py-3",
        "cursor-pointer transition-all duration-300",
        "font-[family-name:var(--font-display)]",
        "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-(--fg-brand)",
        selected
          ? "border-(--fg-brand) shadow-[inset_0_0_0_1px_var(--fg-brand)]"
          : "border-(--border-default) hover:border-(--border-strong) hover:bg-(--bg-sky)",
      ].join(" ")}
    >
      <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-[11px]">
        <input
          type={indicator}
          name={indicator === "radio" ? inputName : undefined}
          checked={selected}
          onChange={() => {
            if (indicator === "checkbox" || !selected) onSelect();
          }}
          onKeyDown={handleKeyDown}
          className="sr-only"
        />
        <span
          aria-hidden="true"
          className={[
            "mt-[1px] flex h-[18px] w-[18px] shrink-0 items-center justify-center",
            "border-[1.5px]",
            indicator === "checkbox" ? "rounded-[5px]" : "rounded-full",
            selected
              ? "border-(--fg-brand)"
              : "border-(--border-strong) text-transparent",
            selected && indicator === "checkbox"
              ? "bg-(--brand-navy) text-white"
              : "",
          ].join(" ")}
        >
          {indicator === "checkbox" ? (
            <Check size={13} />
          ) : (
            selected && (
              <span className="h-2.5 w-2.5 rounded-full bg-(--brand-navy)" />
            )
          )}
        </span>

        {adornment && <span className="shrink-0">{adornment}</span>}

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-(--fg-primary)">
              {name}
            </span>
            {secondaryLabel && (
              <span className="text-[12px] text-(--fg-muted)">
                {secondaryLabel}
              </span>
            )}
          </span>
          {description && (
            <span className="mt-1 text-[13px] leading-[1.5] text-(--fg-secondary)">
              {description}
            </span>
          )}
          {footer}
        </span>
      </label>

      {meta && <span className="shrink-0">{meta}</span>}
    </div>
  );
}
